
import { supabase } from '@/lib/supabase';

export class AffiliateDataMigrationService {
  /**
   * Get affiliate default limits with fallback
   */
  private static async getAffiliateDefaults(affiliateId: string): Promise<{
    daily_limit: number;
    monthly_limit: number;
  }> {
    try {
      console.log(`🔍 Getting defaults for affiliate: ${affiliateId}`);
      
      // Use consolidated RPC function
      const { data, error } = await supabase.rpc(
        'get_affiliate_effective_defaults',
        { affiliate_uuid: affiliateId }
      );

      if (error) {
        console.warn(`⚠️ RPC function error for ${affiliateId}:`, error);
        return { daily_limit: 3, monthly_limit: 20 }; // Fallback defaults
      }

      if (data && data.length > 0 && data[0]) {
        const defaults = data[0];
        console.log(`✅ Got RPC defaults:`, defaults);
        return {
          daily_limit: defaults.daily_limit || 3,
          monthly_limit: defaults.monthly_limit || 20
        };
      }

      console.log(`📋 No RPC data, using fallback defaults`);
      return { daily_limit: 3, monthly_limit: 20 }; // Fallback defaults
      
    } catch (error) {
      console.error(`❌ Exception getting defaults for ${affiliateId}:`, error);
      return { daily_limit: 3, monthly_limit: 20 }; // Fallback defaults
    }
  }

  /**
   * Backfill affiliates.user_id column with affiliate.id values
   */
  static async backfillAffiliatesUserId(): Promise<{
    success: boolean;
    updated: number;
    errors: string[];
  }> {
    try {
      console.log('🔄 Starting backfill of affiliates.user_id...');
      
      // Get affiliates where user_id is null
      const { data: affiliatesWithoutUserId, error: selectError } = await supabase
        .from('affiliates')
        .select('id')
        .is('user_id', null);

      if (selectError) {
        console.error('❌ Error fetching affiliates with null user_id:', selectError);
        return { success: false, updated: 0, errors: [selectError.message] };
      }

      const nullUserIdCount = affiliatesWithoutUserId?.length || 0;
      console.log(`📋 Found ${nullUserIdCount} affiliates with null user_id`);

      if (nullUserIdCount === 0) {
        console.log('✅ No affiliates need user_id backfill');
        return { success: true, updated: 0, errors: [] };
      }

      // Update each affiliate individually to set user_id = id
      let updated = 0;
      const errors: string[] = [];

      for (const affiliate of affiliatesWithoutUserId) {
        try {
          const { error: updateError } = await supabase
            .from('affiliates')
            .update({ user_id: affiliate.id })
            .eq('id', affiliate.id)
            .is('user_id', null);

          if (updateError) {
            console.error(`❌ Error updating affiliate ${affiliate.id}:`, updateError);
            errors.push(`Failed to update affiliate ${affiliate.id}: ${updateError.message}`);
          } else {
            updated++;
            console.log(`✅ Updated affiliate ${affiliate.id}`);
          }
        } catch (error) {
          console.error(`❌ Exception updating affiliate ${affiliate.id}:`, error);
          errors.push(`Exception updating affiliate ${affiliate.id}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      console.log(`✅ Backfill completed: ${updated} affiliates.user_id updated`);
      
      return {
        success: errors.length === 0,
        updated,
        errors
      };
    } catch (error) {
      console.error('❌ Exception during affiliates.user_id backfill:', error);
      return { 
        success: false, 
        updated: 0, 
        errors: [error instanceof Error ? error.message : 'Unknown error'] 
      };
    }
  }

  /**
   * Sync referred_by_affiliate from referrals table to user_profiles
   */
  static async syncReferredByAffiliate(): Promise<{
    success: boolean;
    updated: number;
    errors: string[];
  }> {
    try {
      console.log('🔄 Starting sync of referred_by_affiliate...');
      
      // Step 1: Get all referrals
      const { data: referrals, error: referralsError } = await supabase
        .from('referrals')
        .select('referred_user_id, referrer_id');

      if (referralsError) {
        console.error('❌ Error fetching referrals:', referralsError);
        return { success: false, updated: 0, errors: [referralsError.message] };
      }

      console.log(`📋 Found ${referrals?.length || 0} referral records`);

      const errors: string[] = [];
      let updated = 0;

      // Step 2: Process each referral
      for (const referral of referrals || []) {
        try {
          // Get current user profile
          const { data: userProfile, error: profileError } = await supabase
            .from('user_profiles')
            .select('referred_by_affiliate')
            .eq('user_id', referral.referred_user_id)
            .single();

          if (profileError) {
            if (profileError.code === 'PGRST116') {
              console.log(`⚠️ User profile not found for ${referral.referred_user_id}, skipping`);
              continue;
            }
            errors.push(`Failed to get profile for user ${referral.referred_user_id}: ${profileError.message}`);
            continue;
          }

          // Check if update is needed
          if (userProfile.referred_by_affiliate !== referral.referrer_id) {
            console.log(`🔧 Updating user ${referral.referred_user_id}: ${userProfile.referred_by_affiliate} -> ${referral.referrer_id}`);
            
            const { error: updateError } = await supabase
              .from('user_profiles')
              .update({ referred_by_affiliate: referral.referrer_id })
              .eq('user_id', referral.referred_user_id);

            if (updateError) {
              errors.push(`Failed to update user ${referral.referred_user_id}: ${updateError.message}`);
            } else {
              updated++;
            }
          }
        } catch (error) {
          errors.push(`Exception processing user ${referral.referred_user_id}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      console.log(`✅ Sync completed: ${updated} updated, ${errors.length} errors`);
      
      return {
        success: errors.length === 0,
        updated,
        errors
      };
    } catch (error) {
      console.error('❌ Exception during sync:', error);
      return { 
        success: false, 
        updated: 0, 
        errors: [error instanceof Error ? error.message : 'Unknown error'] 
      };
    }
  }

  /**
   * Backfill missing affiliate_user_limits records
   */
  static async backfillUserLimits(): Promise<{
    success: boolean;
    created: number;
    errors: string[];
  }> {
    try {
      console.log('🔄 Starting backfill of user limits...');
      
      // Step 1: Get all referrals
      const { data: referrals, error: referralsError } = await supabase
        .from('referrals')
        .select('referred_user_id, referrer_id');

      if (referralsError) {
        console.error('❌ Error fetching referrals:', referralsError);
        return { success: false, created: 0, errors: [referralsError.message] };
      }

      console.log(`📋 Found ${referrals?.length || 0} referral records to process`);

      const errors: string[] = [];
      let created = 0;

      // Step 2: Process each referral
      for (const referral of referrals || []) {
        try {
          // Check if limits already exist
          const { data: existingLimits, error: limitsError } = await supabase
            .from('affiliate_user_limits')
            .select('id')
            .eq('user_id', referral.referred_user_id)
            .eq('affiliate_id', referral.referrer_id)
            .eq('feature_type', 'market_analysis')
            .single();

          if (limitsError && limitsError.code !== 'PGRST116') {
            errors.push(`Failed to check existing limits for user ${referral.referred_user_id}: ${limitsError.message}`);
            continue;
          }

          // Skip if limits already exist
          if (existingLimits) {
            console.log(`⏭️ Limits already exist for user ${referral.referred_user_id}`);
            continue;
          }

          console.log(`🔧 Creating limits for user ${referral.referred_user_id} under affiliate ${referral.referrer_id}`);
          
          // Get affiliate's default limits
          const defaultLimits = await this.getAffiliateDefaults(referral.referrer_id);
          
          // Create the limits record
          const limitRecord = {
            affiliate_id: referral.referrer_id,
            user_id: referral.referred_user_id,
            feature_type: 'market_analysis',
            daily_limit: defaultLimits.daily_limit,
            monthly_limit: defaultLimits.monthly_limit,
            is_default: true,
            updated_at: new Date().toISOString()
          };

          console.log(`📝 Inserting limit record:`, limitRecord);

          const { error: insertError } = await supabase
            .from('affiliate_user_limits')
            .insert(limitRecord);

          if (insertError) {
            console.error(`❌ Insert error for user ${referral.referred_user_id}:`, insertError);
            errors.push(`Failed to create limits for user ${referral.referred_user_id}: ${insertError.message}`);
          } else {
            console.log(`✅ Successfully created limits for user ${referral.referred_user_id}`);
            created++;
          }
        } catch (error) {
          console.error(`❌ Exception processing user ${referral.referred_user_id}:`, error);
          errors.push(`Exception processing user ${referral.referred_user_id}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      console.log(`✅ Backfill completed: ${created} created, ${errors.length} errors`);
      
      return {
        success: errors.length === 0,
        created,
        errors
      };
    } catch (error) {
      console.error('❌ Exception during backfill:', error);
      return { 
        success: false, 
        created: 0, 
        errors: [error instanceof Error ? error.message : 'Unknown error'] 
      };
    }
  }

  /**
   * Run full data migration and consistency check
   */
  static async runFullMigration(): Promise<{
    success: boolean;
    userIdBackfillResult: any;
    syncResult: any;
    backfillResult: any;
    consistencyCheck: any;
  }> {
    try {
      console.log('🚀 Starting full affiliate data migration...');

      // Step 1: Backfill affiliates.user_id
      const userIdBackfillResult = await this.backfillAffiliatesUserId();
      
      // Step 2: Sync referred_by_affiliate
      const syncResult = await this.syncReferredByAffiliate();
      
      // Step 3: Backfill user limits
      const backfillResult = await this.backfillUserLimits();
      
      // Step 4: Run consistency check
      const { userAssignmentService } = await import('./userAssignmentService');
      const consistencyCheck = await userAssignmentService.validateDataConsistency();

      const overallSuccess = userIdBackfillResult.success && syncResult.success && backfillResult.success && consistencyCheck.isConsistent;

      console.log('🏁 Full migration completed:', {
        overallSuccess,
        userIdUpdated: userIdBackfillResult.updated,
        syncUpdated: syncResult.updated,
        backfillCreated: backfillResult.created,
        consistencyIssues: consistencyCheck.issues.length
      });

      return {
        success: overallSuccess,
        userIdBackfillResult,
        syncResult,
        backfillResult,
        consistencyCheck
      };
    } catch (error) {
      console.error('❌ Exception during full migration:', error);
      throw error;
    }
  }
}
